import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import images from "../../../assets/images";
import axiosInstance from "../../../common/axiosInstance";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import { PageLoader } from "../../../components/loader/Loader";
import {
  ColDef,
  ICellRendererParams,
  ValueGetterParams,
} from "ag-grid-community";
import { usePremisesStore } from "../../../store/store";

interface ChecklistItem {
  question: string;
  type: string;
  weightage: number;
  categoryId: string;
}

interface Checklist {
  _id: string;
  name: string;
  premisesId: string;
  materialType?: string;
  items: ChecklistItem[];
  createdAt: string;
  updatedAt: string;
  premisesName?: string;
}

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const HazMatChecklists = () => {
  const navigate = useNavigate();
  const [rowData, setRowData] = useState<Checklist[]>([]);
  const [originalRowData, setOriginalRowData] = useState<Checklist[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedPremises, setSelectedPremises] = useState("");
  const { allPremises, fetchPremises } = usePremisesStore();

  const fetchChecklists = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/hazmat-checklist-config`);

      if (response.status === 200) {
        const checklistsWithPremises = (response.data.data || []).map(
          (checklist: Checklist) => {
            const premise = allPremises.find(
              (p) => p.id === checklist.premisesId
            );
            return {
              ...checklist,
              premisesName: premise?.premisesName || "N/A",
            };
          }
        );
        setOriginalRowData(checklistsWithPremises);
        setRowData(checklistsWithPremises);
      }
    } catch (error) {
      console.error("Error fetching checklists:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allPremises.length === 0) {
      fetchPremises();
    }
  }, []);

  useEffect(() => {
    if (allPremises.length > 0) {
      fetchChecklists();
    }
  }, [allPremises]);

  const handleSearch = async (keyword: string) => {
    if (!keyword.trim()) {
      setRowData(originalRowData);
      return;
    }

    setLoading(true);
    const filteredData = originalRowData.filter(
      (item) =>
        item.name.toLowerCase().includes(keyword.toLowerCase()) ||
        item.premisesName?.toLowerCase().includes(keyword.toLowerCase()) ||
        (item.materialType &&
          item.materialType.toLowerCase().includes(keyword.toLowerCase())) ||
        item.items.some((item) =>
          item.question.toLowerCase().includes(keyword.toLowerCase())
        ) ||
        item.items.some((item) =>
          item.type.toLowerCase().includes(keyword.toLowerCase())
        ) ||
        item.items.some((item) =>
          item.weightage.toString().includes(keyword.toLowerCase())
        )
    );
    setRowData(filteredData);
    setLoading(false);
  };

  const handleNavigate = (id: string, mode: "view" | "edit") => {
    navigate(`/hazardous-materials/checklists/${mode}/${id}`);
  };

  const columnDefs = useMemo<ColDef<Checklist>[]>(
    () => [
      {
        field: "name",
        headerName: "Checklist Name",
        flex: 1,
        minWidth: 200,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 0 0 15px" },
      } as ColDef<Checklist>,
      {
        field: "premisesName",
        headerName: "Premises",
        flex: 1,
        minWidth: 200,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 0 0 15px" },
      } as ColDef<Checklist>,
      {
        field: "materialType",
        headerName: "Material Type",
        flex: 1,
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 0 0 15px" },
        valueGetter: (params: ValueGetterParams<Checklist>) =>
          params.data?.materialType || "N/A",
      } as ColDef<Checklist>,
      {
        field: "items",
        headerName: "Questions",
        flex: 1,
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 0 0 15px" },
        valueGetter: (params: ValueGetterParams<Checklist>) =>
          params.data?.items?.length || 0,
      } as ColDef<Checklist>,
      {
        field: "updatedAt",
        headerName: "Last Updated",
        flex: 1,
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 0 0 15px" },
        valueGetter: (params: ValueGetterParams<Checklist>) =>
          params.data
            ? new Date(params.data.updatedAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
            : "",
      } as ColDef<Checklist>,
      {
        headerName: "Actions",
        flex: 1,
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 0 0 15px" },
        cellRenderer: (params: ICellRendererParams<Checklist>) => {
          const data = params.data;
          if (!data) return null;

          return (
            <div className="flex gap-x-3">
              <button
                onClick={() => handleNavigate(data._id, "view")}
                className="p-1 hover:bg-gray-100 rounded-full"
                title="View Checklist"
              >
                <img className="w-5 h-5" src={images.viewIcon} alt="View" />
              </button>
              <button
                onClick={() => handleNavigate(data._id, "edit")}
                className="p-1 hover:bg-gray-100 rounded-full"
                title="Edit Checklist"
              >
                <img className="w-5 h-5" src={images.editIcon} alt="Edit" />
              </button>
            </div>
          );
        },
      } as ColDef<Checklist>,
    ],
    [navigate]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    []
  );

  return (
    <div className="px-2 py-4 mt-14 ml-2 min-h-100vh">
      <div className="flex justify-between items-center mb-4">
        <h2 className="mb-3">Hazardous Material Checklists</h2>
        <button
          onClick={() => navigate("/hazardous-materials/checklists/create")}
          className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
        >
          CREATE CHECKLIST
        </button>
      </div>

      <div className="tableBlock w-full">
        <div className="card">
          <div className="tableSearchBlock flex justify-between items-center">
            <div className="leftTableSearchBlock">
              <div className="input-affix">
                <i className="prefix-icon anticon anticon-search"></i>
                <input
                  type="text"
                  className="form-control"
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search Here..."
                />
              </div>
            </div>

            <span className="flex gap-8">
              <Dropdown
                selectedValue={selectedRegion}
                onChange={(value) => {
                  setSelectedRegion(value);
                  setSelectedPremises("");
                }}
                type="region"
              />
              <Dropdown
                selectedValue={selectedPremises}
                onChange={(value) => {
                  setSelectedPremises(value);
                }}
                type="premises"
                selectedRegion={selectedRegion}
              />
            </span>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <PageLoader />
            </div>
          ) : rowData.length > 0 ? (
            <div className="ag-theme-alpine w-full border-0 rounded-lg shadow-lg">
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                theme={gridTheme}
                headerHeight={65}
                rowHeight={50}
                defaultColDef={defaultColDef}
                pagination={true}
                paginationPageSize={10}
                domLayout="autoHeight"
              />
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center h-64">
              <p className="text-gray-500 text-lg mb-4">No checklists found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HazMatChecklists;
